import { useMemo } from "react";
import { X, MapPin, Calendar, DollarSign, Clock, Send } from "lucide-react";
import ModalOverlay from "../UI/ModalOverlay/ModalOverlay.tsx";
import { InternalWorkOffer } from "./InternalWorkOfferCard";
import styles from "./WorkOfferDetailsModal.module.scss";

export const formatPostedTime = (createdAt?: string) => {
  if (!createdAt) return "Reciente";
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return "Reciente";
  const diff = Date.now() - created.getTime();
  if (diff < 60 * 1000) return "Hace instantes";
  if (diff < 60 * 60 * 1000) {
    const minutes = Math.floor(diff / (60 * 1000));
    return `Hace ${minutes} min`;
  }
  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diff / (60 * 60 * 1000));
    return `Hace ${hours} h`;
  }
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  return `Hace ${days} día${days === 1 ? "" : "s"}`;
};

export const formatDate = (date?: string) => {
  if (!date) return "Sin dato";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "Sin dato";
  return parsed.toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" });
};

export const formatSalary = (salary?: string, type?: string) => {
  if (!salary || salary === "0.00" || Number.parseFloat(salary) === 0) return "A convenir";
  const base = Math.floor(Number.parseFloat(salary)).toLocaleString("es-AR");
  switch (type) {
    case "Hour":
      return `$${base} / hora`;
    case "Day":
      return `$${base} / día`;
    case "Week":
      return `$${base} / semana`;
    case "Month":
      return `$${base} / mes`;
    case "Job":
      return `$${base} / proyecto`;
    default:
      return `$${base}`;
  }
};

type DetailsContentProps = {
  offer: InternalWorkOffer;
  onClose?: () => void;
  onApply?: (offer: InternalWorkOffer) => void;
  showApplyButton?: boolean;
  applyDisabled?: boolean;
};

export const WorkOfferDetailsContent = ({
  offer,
  onClose,
  onApply,
  showApplyButton = true,
  applyDisabled = false,
}: DetailsContentProps) => {
  const companyLabel = useMemo(() => {
    if (!offer?.user) return "Publicador";
    if (offer.user.type === "COMPANY") return offer.user.razon_social ?? "Empresa";
    const name = offer.user.name ?? "";
    const last = offer.user.last_name ?? "";
    const full = `${name} ${last}`.trim();
    return full || "Publicador";
  }, [offer?.user]);

  const primaryZone = offer?.workZones?.[0]?.jurisdiccion?.name ?? offer?.workZones?.[0]?.city ?? "Ubicación a confirmar";
  const startLabel = offer?.immediateStart ? "Inicio inmediato" : formatDate(offer?.startDate);
  const activeUntil = formatDate(offer?.activeUntil);
  const postedLabel = formatPostedTime(offer?.createdAt);
  const salaryLabel = formatSalary(offer?.salary, offer?.salaryType);

  return (
    <div className={styles.modal}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h2 className={styles.title}>Detalles de la oferta</h2>
          <span className={styles.timestamp}>{postedLabel}</span>
        </div>
        {onClose && (
          <button type="button" className={styles.close} onClick={onClose} aria-label="Cerrar detalles">
            <X size={18} />
          </button>
        )}
      </header>

      <div className={styles.body}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Publicada por</h3>
          <span className={styles.company}>{companyLabel}</span>
        </section>

        {offer.description && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Descripción</h3>
            <p className={styles.description}>{offer.description}</p>
          </section>
        )}

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Información clave</h3>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <MapPin size={16} />
              <span>{primaryZone}</span>
            </div>
            <div className={styles.infoItem}>
              <Calendar size={16} />
              <span>{startLabel}</span>
            </div>
            <div className={styles.infoItem}>
              <Clock size={16} />
              <span>Visible hasta {activeUntil}</span>
            </div>
            <div className={styles.infoItem}>
              <DollarSign size={16} />
              <span>{salaryLabel}</span>
            </div>
          </div>
        </section>

        {offer.skill && offer.skill.length > 0 && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Rubro requerido</h3>
            <div className={styles.chipGroup}>
              {offer.skill.map((item, index) => {
                const name = item?.skill?.name ?? `Rubro ${index + 1}`;
                return (
                  <span key={`${offer.id}-skill-${index}`} className={styles.chip}>
                    {name}
                  </span>
                );
              })}
            </div>
          </section>
        )}

        {offer.workZones && offer.workZones.length > 0 && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Zonas de trabajo</h3>
            <div className={styles.tagList}>
              {offer.workZones.map((zone, index) => {
                const label = zone?.jurisdiccion?.name ?? zone?.city ?? `Zona ${index + 1}`;
                return (
                  <span key={`${offer.id}-zone-${index}`} className={styles.tag}>
                    {label}
                  </span>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {(onClose || onApply) && (
        <footer className={styles.footer}>
          {onClose && (
            <button type="button" className={styles.secondary} onClick={onClose}>
              Cerrar
            </button>
          )}
          {showApplyButton && onApply && (
            <button
              type="button"
              className={styles.primary}
              onClick={() => onApply(offer)}
              disabled={applyDisabled}
            >
              <Send size={16} />
              Postularme
            </button>
          )}
        </footer>
      )}
    </div>
  );
};

type WorkOfferDetailsModalProps = {
  offer: InternalWorkOffer | null;
  isOpen: boolean;
  onClose: () => void;
  onApply?: (offer: InternalWorkOffer) => void;
};

const WorkOfferDetailsModal = ({ offer, isOpen, onClose, onApply }: WorkOfferDetailsModalProps) => {
  if (!offer) {
    return null;
  }

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose} zIndex={40}>
      <WorkOfferDetailsContent offer={offer} onClose={onClose} onApply={onApply} />
    </ModalOverlay>
  );
};

export default WorkOfferDetailsModal;
